import type { LpcLayers } from "../../infra/api.js";
import {
  ACCESSORY_STYLES,
  BODY_STYLES,
  FABRIC_COLORS,
  HAIR_COLORS,
  HAIR_STYLES,
  PANTS_STYLES,
  SHIRT_STYLES,
} from "./LpcAvatar.js";

// Client-side validation that mirrors the backend Zod schema in
// apps/studio-api/src/modules/artists/types.ts. Keep these two in sync:
// the server rejects anything this misses, so divergence only ever costs
// the user a round-trip — never a corrupt save.

// Backend: name is z.string().min(1).max(80).
export const ARTIST_NAME_MAX = 80;

const HAIR_COLOR_IDS = Object.keys(HAIR_COLORS);
const FABRIC_COLOR_IDS = Object.keys(FABRIC_COLORS);

export interface ArtistFieldErrors {
  name?: string;
  layers?: string;
}

export function validateArtistName(name: string): string | undefined {
  const trimmed = name.trim();
  if (trimmed.length === 0) return "Dê um nome ao personagem.";
  if (trimmed.length > ARTIST_NAME_MAX) {
    return `O nome deve ter no máximo ${String(ARTIST_NAME_MAX)} caracteres.`;
  }
  return undefined;
}

// Mirrors lpcLayersSchema: body/hair/shirt/pants required, accessory optional.
// The AvatarBuilder can only produce valid ids, but a stale cache or a future
// regression could not — so we still guard the layer set before submit.
export function validateLpcLayers(layers: LpcLayers): string | undefined {
  if (!(BODY_STYLES as readonly string[]).includes(layers.body.id)) {
    return "Escolha um tom de pele válido.";
  }
  if (!(HAIR_STYLES as readonly string[]).includes(layers.hair.id)) {
    return "Escolha um estilo de cabelo válido.";
  }
  if (layers.hair.id !== "bald" && !HAIR_COLOR_IDS.includes(layers.hair.color)) {
    return "Escolha uma cor de cabelo válida.";
  }
  if (!(SHIRT_STYLES as readonly string[]).includes(layers.shirt.id)) {
    return "Escolha uma camisa válida.";
  }
  if (!FABRIC_COLOR_IDS.includes(layers.shirt.color)) {
    return "Escolha uma cor de camisa válida.";
  }
  if (!(PANTS_STYLES as readonly string[]).includes(layers.pants.id)) {
    return "Escolha uma calça válida.";
  }
  if (!FABRIC_COLOR_IDS.includes(layers.pants.color)) {
    return "Escolha uma cor de calça válida.";
  }
  if (
    layers.accessory !== undefined &&
    !(ACCESSORY_STYLES as readonly string[]).includes(layers.accessory.id)
  ) {
    return "Escolha um acessório válido.";
  }
  return undefined;
}

export function validateArtist(name: string, layers: LpcLayers): ArtistFieldErrors {
  const errors: ArtistFieldErrors = {};
  const nameError = validateArtistName(name);
  if (nameError) errors.name = nameError;
  const layersError = validateLpcLayers(layers);
  if (layersError) errors.layers = layersError;
  return errors;
}

export function isArtistValid(errors: ArtistFieldErrors): boolean {
  return errors.name === undefined && errors.layers === undefined;
}
